import { supabase } from "@/integrations/supabase/client";
import { trackGA4Event } from "@/lib/ga4";
import { trackPostHogEvent } from "@/lib/posthog";
import type { AnyValue } from "@/lib/rowTypes";

function getCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : undefined;
}

function makeEventId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// Mirror of meta-capi ALLOWED_EVENTS. Standard events go through fbq("track")
// + server CAPI. Anything else is treated as a custom event: fbq("trackCustom")
// only, no CAPI call (CAPI rejects non-standard names with 400).
const STANDARD_META_EVENTS = new Set([
  "PageView",
  "Lead",
  "CompleteRegistration",
  "Subscribe",
  "Purchase",
  "StartTrial",
  "InitiateCheckout",
]);

type TrackOptions = {
  email?: string;
  externalId?: string;
  value?: number;
  currency?: string;
  customData?: Record<string, AnyValue>;
};

/**
 * Fires a Meta event both via the browser Pixel (fbq) and the server-side
 * Conversions API. Uses a shared event_id for deduplication.
 */
export async function trackMetaEvent(eventName: string, opts: TrackOptions = {}) {
  const eventId = makeEventId();
  const url = typeof window !== "undefined" ? window.location.href : undefined;
  const isStandard = STANDARD_META_EVENTS.has(eventName);

  // Browser pixel
  try {
    const fbq = (window as AnyValue).fbq;
    if (typeof fbq === "function") {
      const params: Record<string, AnyValue> = {};
      if (opts.value != null) params.value = opts.value;
      if (opts.currency) params.currency = opts.currency;
      Object.assign(params, opts.customData ?? {});
      fbq(isStandard ? "track" : "trackCustom", eventName, params, { eventID: eventId });
    }
  } catch {
    /* noop */
  }

  // GA4 mirror — silent no-op when gtag isn't loaded. Email is intentionally
  // omitted from GA4; only Meta CAPI needs the hashed identifier.
  trackGA4Event(eventName, {
    event_id: eventId,
    value: opts.value,
    currency: opts.currency,
    ...(opts.customData ?? {}),
  });

  // PostHog mirror — additive, sanitized, silent no-op when PostHog isn't
  // initialized. `PageView` is deliberately EXCLUDED: PostHogRouteTracker is
  // the sole source of PostHog `$pageview` events, so mirroring it here would
  // double-count SPA navigations.
  if (eventName !== "PageView") {
    trackPostHogEvent(eventName, {
      event_id: eventId,
      value: opts.value,
      currency: opts.currency,
      ...(opts.customData ?? {}),
    });
  }



  // Server-side CAPI — only standard events. CAPI allowlist rejects custom
  // names with 400, so we skip the round-trip for those.
  if (!isStandard) return;
  try {
    // CAPI requires an authenticated end-user JWT to prevent abuse.
    // Skip the server round-trip for anonymous visitors (e.g. landing/login pages);
    // the browser pixel above already captured the event.
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    await supabase.functions.invoke("meta-capi", {
      body: {
        event_name: eventName,
        event_id: eventId,
        event_source_url: url,
        email: opts.email,
        external_id: opts.externalId,
        value: opts.value,
        currency: opts.currency,
        custom_data: opts.customData,
        fbp: getCookie("_fbp"),
        fbc: getCookie("_fbc"),
      },
    });
  } catch {
    /* noop — do not block UX on tracking */
  }
}